import React, { useEffect, useState, useCallback } from "react";
import { PasswordInput } from "../Input";
import { t } from "i18next";
import CustomButton from "ui/Button";
import { COLOR } from "utils/constant";
import { useUserStore } from "store";
import { changePasswordApi, ChangePasswordForm } from "api/auth";
import { CustomError } from "interface";
import { useInfoStore } from "store/error";
import { useWindowWidth } from "hooks/hooks";

interface PasswordRules {
	minLength: boolean;
	hasUpper: boolean;
	hasLower: boolean;
	hasDigit: boolean;
	hasSpecial: boolean;
}

const validatePassword = (password: string): PasswordRules => ({
	minLength: password.length >= 6,
	hasUpper: /[A-Z]/.test(password),
	hasLower: /[a-z]/.test(password),
	hasDigit: /\d/.test(password),
	hasSpecial: /[!@#$%^&*()_+\[\]{}|;:,.<>?]/.test(password),
});

export const ChangePassword: React.FC = () => {
	const [form, setForm] = useState<ChangePasswordForm>({
		oldPassword: "",
		newPassword: "",
		confirmPassword: "",
		userName: "",
	});
	const [error, setError] = useState<string>("");
	const [passwordChanged, setPasswordChanged] = useState<boolean>(false);
	const [rules, setRules] = useState<PasswordRules>({
		minLength: false,
		hasUpper: false,
		hasLower: false,
		hasDigit: false,
		hasSpecial: false,
	});
	const { user } = useUserStore();
	const { setError: setErrorInfo, setInfo, resetStore } = useInfoStore();
	const windowWidth = useWindowWidth();
	const isMobileView = windowWidth < 768;

	useEffect(() => {
		if (user) {
			setForm((prev) => ({ ...prev, userName: user.userName }));
		}
	}, [user]);

	const onNewPasswordChange = useCallback((value: string) => {
		setForm((prev) => ({ ...prev, newPassword: value }));
		setRules(validatePassword(value));
	}, []);

	const handleSubmit = () => {
		if (!form.oldPassword || !form.newPassword || !form.confirmPassword) {
			setError(t("AllFieldsRequired"));
			return;
		}
		if (form.newPassword !== form.confirmPassword) {
			setError(t("PasswordMismatch"));
			return;
		}
		const currentRules = validatePassword(form.newPassword);
		if (
			!currentRules.minLength ||
			!currentRules.hasUpper ||
			!currentRules.hasLower ||
			!currentRules.hasDigit ||
			!currentRules.hasSpecial
		) {
			setError(t("PasswordRequirementsNotMet"));
			return;
		}
		setError("");
		changePasswordApi({
			oldPassword: form.oldPassword,
			newPassword: form.newPassword,
			userName: form.userName,
		})
			.then(() => {
				setForm({
					oldPassword: "",
					newPassword: "",
					confirmPassword: "",
					userName: "",
				});
				setInfo({
					title: t("Success"),
					description: t("ChangePasswordSuccess"),
					show: true,
					actions: [
						{
							text: t("Ok"),
							btn: () => resetStore(),
							bgColor: COLOR.Green,
							textColor: "white",
						},
					],
					infoType: "Success",
				});
				setPasswordChanged(true);
			})
			.catch((error) => {
				const err = new CustomError(
					t("PasswordChangeFailed", "PasswordChangeFailed")
				);
				const showCustomError = true;
				setErrorInfo(err, showCustomError);
				setError(t("PasswordChangeFailed"));
			});
	};

	const renderRuleItem = (label: string, passed: boolean) => (
		<div style={{ color: passed ? "green" : "red", marginBottom: "4px" }}>
			{passed ? "✔" : "✖"} {label}
		</div>
	);

	return (
		<div style={{ width: "100%", maxWidth: "1000px" }}>
			<div className="fontWork22">{t("ChangePassword")}</div>
			{error && <div style={{ color: "red" }}>{error}</div>}
			<div
				style={{
					display: "flex",
					flexDirection: isMobileView ? "column" : "row",
					gap: "20px",
				}}
			>
				<div style={{ flex: 1 }}>
					<div>
						<label>{t("OlderPassword")}</label>
						<PasswordInput
							containerStyle={{ width: "100%", maxWidth: "500px" }}
							value={form.oldPassword}
							onChange={(value) =>
								setForm((prev) => ({ ...prev, oldPassword: value }))
							}
							setPasswordValidated={() => {}}
						/>
					</div>
					<div style={{ marginTop: "10px" }}>
						<label>{t("NewPassword")}</label>
						<PasswordInput
							containerStyle={{ width: "100%", maxWidth: "500px" }}
							value={form.newPassword}
							onChange={onNewPasswordChange}
							setPasswordValidated={() => {}}
						/>
					</div>
					<div style={{ marginTop: "10px" }}>
						<label>{t("ConfirmNewPassword")}</label>
						<PasswordInput
							containerStyle={{ width: "100%", maxWidth: "500px" }}
							value={form.confirmPassword}
							onChange={(value) =>
								setForm((prev) => ({ ...prev, confirmPassword: value }))
							}
							setPasswordValidated={() => {}}
						/>
					</div>
				</div>
				<div
					style={{
						width: isMobileView ? "100%" : "300px",
						paddingTop: isMobileView ? "0px" : "35px",
					}}
				>
					{renderRuleItem(t("PasswordAtleast"), rules.minLength)}
					{renderRuleItem(t("PasswordUppercase"), rules.hasUpper)}
					{renderRuleItem(t("PasswordLowercase"), rules.hasLower)}
					{renderRuleItem(t("PasswordDigit"), rules.hasDigit)}
					{renderRuleItem(t("PasswordSpecial"), rules.hasSpecial)}
				</div>
			</div>
			<div style={{ marginTop: "20px" }}>
				<CustomButton
					label={t("ChangePassword")}
					bgColor={COLOR.Green}
					color="white"
					onClick={handleSubmit}
					btnStyle={isMobileView ? { width: "100%" } : undefined}
				/>
			</div>
			{passwordChanged && (
				<div style={{ color: "green", textAlign: "center" }}>
					{t("ChangePasswordSuccess")}
				</div>
			)}
		</div>
	);
};
